import React, { FC, useState } from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';

import RemoveFillIcon from '@ad-scripts/presentation/components/common/icons/fill/remove-fill';
import { ModalSize } from 'src/presentation/components/pages/shared/EmbedModal';
import EmbedModalWithGreenHeader from 'src/presentation/components/pages/shared/EmbedModalWithGreenHeader';
import DeleteButton from 'src/presentation/components/pages/shared/DeleteButton';
import { Button, ButtonTypes } from 'src/presentation/components/agent_desktop_components';

export type DeleteProps = {
  id: number | string;
  body: React.ReactNode;
  cancelText?: string;
  dataAidPrefix: string;
  deleteAction?: Function;
  deleteIcon?: any;
  title: string;
  onDelete?: Function;
  submitText?: string;
  tooltip?: string;
};

type ModalProps = {
  body: React.ReactNode;
  cancelText: string;
  dataAidPrefix: string;
  onCancel: Function;
  onSubmit: Function;
  submitText: string;
};

const Delete: FC<DeleteProps> = ({
  id,
  body,
  cancelText,
  dataAidPrefix,
  deleteAction,
  deleteIcon,
  onDelete,
  submitText,
  title,
  tooltip,
}) => {
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const dispatch = useDispatch();

  const openModal = function () {
    setIsDeleteModalVisible(true);
  };

  const handleCancel = function () {
    setIsDeleteModalVisible(false);
  };

  const handleSubmit = function () {
    if (onDelete) {
      onDelete({ id });
    } else if (deleteAction) {
      dispatch(deleteAction({ id }));
    }
    setIsDeleteModalVisible(false);
  };

  return (
    <>
      <DeleteButton
        id={id}
        onClick={openModal}
        icon={deleteIcon || StyledTrashIcon}
        message={
          <TooltipMessage data-aid={`${dataAidPrefix}-delete-tooltip-text`}>
            {tooltip || 'Remove from Gladly'}
          </TooltipMessage>
        }
      />
      {isDeleteModalVisible && (
        <StyledModal
          data-aid={`${dataAidPrefix}-delete-modal`}
          title={title}
          modalSize={ModalSize.EXTRALARGE}
          content={ModalContent({
            body,
            cancelText: cancelText || 'Cancel',
            dataAidPrefix,
            onCancel: handleCancel,
            onSubmit: handleSubmit,
            submitText: submitText || 'Remove',
          })}
          disableOutsideClick={true}
          onCancel={handleCancel}
        />
      )}
    </>
  );
};

const ModalContent: FC<ModalProps> = ({ body, cancelText, dataAidPrefix, onCancel, onSubmit, submitText }) => {
  return (
    <>
      <BodyContainer>
        <ConfirmationText>{body}</ConfirmationText>
      </BodyContainer>
      <Footer>
        <Button data-aid={`${dataAidPrefix}-submit-delete`} type={ButtonTypes.PRIMARY} onClick={onSubmit}>
          {submitText}
        </Button>
        <Button data-aid={`${dataAidPrefix}-cancel-delete`} type={ButtonTypes.SECONDARY} onClick={onCancel}>
          {cancelText}
        </Button>
      </Footer>
    </>
  );
};
const StyledModal = styled(EmbedModalWithGreenHeader)`
  padding: 0px;
`;

const Footer = styled.div`
  display: flex;
  flex-direction: row-reverse;
  align-items: center;
  gap: 10px;
  padding: ${({ theme }) => theme.spacing.insetLarge};
  padding-right: 18px;
`;

const BodyContainer = styled.div`
  padding: ${({ theme }) => theme.spacing.insetLarge};
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray300};
`;

const ConfirmationText = styled.p`
  text-align: left;
  margin: 0;
  line-height: 162%;
`;

const TooltipMessage = styled.p`
  text-align: center;
  margin: 0;
  line-height: 162%;
`;

const StyledTrashIcon = styled(RemoveFillIcon)`
  fill: ${p => p.theme.colors.gray600};
`;

export default Delete;
